import { CASHFLOW_OUT_SOURCES, type Cashflow, type CashflowOutSource } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { date, tenge } from '../../lib/format';
import { EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import { CashGaps } from './CashGaps';

/** ДДС проекта (или всей компании) по неделям и месяцам: план и факт поступлений и платежей (ФН-01) */
export function CashflowView({ projectId }: { projectId?: string }) {
  const [grain, setGrain] = useState<'week' | 'month'>('week');
  const [opening, setOpening] = useState('');
  const openingNum = Number(opening.replace(/\s/g, '').replace(',', '.'));
  const q = useQuery({
    queryKey: ['finance', 'cashflow', projectId ?? 'all', grain, opening],
    queryFn: () =>
      api<Cashflow>('/finance/cashflow', {
        query: {
          projectId,
          grain,
          opening: opening.trim() && Number.isFinite(openingNum) ? openingNum : undefined,
        },
      }),
    placeholderData: (prev) => prev,
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const c = q.data;
  const row = (label: string, v: number, strong = false) => (
    <div className="flex justify-between gap-3 text-[14px]">
      <span className="text-ink-2">{label}</span>
      <span className={strong ? 'num font-semibold' : 'num'}>{tenge(v)}</span>
    </div>
  );
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-[13px] text-ink-2" htmlFor="cf-grain">
          Период
        </label>
        <Select
          id="cf-grain"
          value={grain}
          onChange={(e) => setGrain(e.target.value as 'week' | 'month')}
          className="max-w-[180px]"
        >
          <option value="week">По неделям</option>
          <option value="month">По месяцам</option>
        </Select>
        <label className="text-[13px] text-ink-2" htmlFor="cf-opening">
          Остаток на начало, ₸
        </label>
        <Input
          id="cf-opening"
          inputMode="decimal"
          value={opening}
          onChange={(e) => setOpening(e.target.value)}
          className="max-w-[160px]"
        />
        <span className="text-[13px] text-ink-2">
          {date(c.from)} — {date(c.to)}
          {projectId ? ' · выбранный проект и его подпроекты' : ' · все доступные проекты'}
        </span>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <Panel title="Поступления">
          <div className="flex flex-col gap-1 p-5">
            {row('Получено', c.totals.inPaid)}
            {row('Ожидается по графикам договоров', c.totals.inPlanned)}
            {c.outside.inPlanned
              ? row('Ожидается вне показанных периодов (просрочено или позже)', c.outside.inPlanned)
              : null}
            {c.undated.inPlanned ? row('Ожидается без даты', c.undated.inPlanned) : null}
          </div>
        </Panel>
        <Panel title="Платежи">
          <div className="flex flex-col gap-1 p-5">
            {row('Оплачено', c.totals.outPaid)}
            {row('К оплате по плану', c.totals.outPlanned)}
            {(Object.keys(CASHFLOW_OUT_SOURCES) as CashflowOutSource[])
              .filter((k) => c.outBySource?.[k])
              .map((k) => (
                <div key={k} className="flex justify-between gap-3 pl-3 text-[13px]">
                  <span className="text-ink-2">{CASHFLOW_OUT_SOURCES[k]}</span>
                  <span className="num">{tenge(c.outBySource?.[k] ?? 0)}</span>
                </div>
              ))}
            {c.outside.outPlanned
              ? row('К оплате вне показанных периодов (просрочено или позже)', c.outside.outPlanned)
              : null}
            {c.undated.outPlanned ? row('К оплате без даты', c.undated.outPlanned) : null}
          </div>
        </Panel>
      </div>
      <CashGaps c={c} projectId={projectId} />
      {!c.periods.length ? (
        <EmptyState title="Нет данных за период" />
      ) : (
        <Panel>
          <ScrollX>
            <table className="w-full min-w-[720px] text-left text-[14px]">
              <thead className="text-[12px] text-ink-2">
                <tr>
                  <th className="py-2.5 pr-3 pl-5">{grain === 'week' ? 'Неделя с' : 'Месяц'}</th>
                  <th className="py-2.5 pr-3 text-right">Поступления</th>
                  <th className="py-2.5 pr-3 text-right">Платежи</th>
                  <th className="py-2.5 pr-3 text-right">Итог</th>
                  <th className="py-2.5 pr-5 text-right">Нарастающим</th>
                </tr>
              </thead>
              <tbody>
                {c.periods.map((p) => (
                  <tr key={p.start} className="border-t border-line">
                    <td className="py-2.5 pr-3 pl-5">{date(p.start)}</td>
                    <td className="num py-2.5 pr-3 text-right">
                      {tenge(p.inPaid + p.inPlanned)}
                      {p.inPlanned ? (
                        <div className="text-[12px] text-ink-2">из них план {tenge(p.inPlanned)}</div>
                      ) : null}
                    </td>
                    <td className="num py-2.5 pr-3 text-right">
                      {tenge(p.outPaid + p.outPlanned)}
                      {p.outPlanned ? (
                        <div className="text-[12px] text-ink-2">из них план {tenge(p.outPlanned)}</div>
                      ) : null}
                    </td>
                    <td className="num py-2.5 pr-3 text-right">{tenge(p.net)}</td>
                    <td className={`num py-2.5 pr-5 text-right ${p.cumulative < 0 ? 'text-danger' : ''}`}>
                      {tenge(p.cumulative)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollX>
        </Panel>
      )}
      <p className="text-[13px] text-ink-2">
        {c.plans?.length
          ? `Персонал, административные расходы и налоги — из утверждённого ФП: ${c.plans
              .map((p) => `${p.projectCode} № ${p.number}`)
              .join(', ')}. `
          : ''}
        {c.withoutPlan?.length
          ? `Нет утверждённого ФП — персонала и налогов в ДДС нет: ${c.withoutPlan
              .slice(0, 8)
              .map((p) => p.projectCode)
              .join(', ')}${c.withoutPlan.length > 8 ? ` и ещё ${c.withoutPlan.length - 8}` : ''}. `
          : ''}
        Пока не учитываются: {c.notCovered.join(', ').toLowerCase()} — для них в портале нет данных. Остаток
        денег на начало периода не известен, поэтому нарастающий итог считается с первого показанного периода.
      </p>
    </div>
  );
}
